import { db, auth } from '@/firebase';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc
} from 'firebase/firestore';
import { UserProfile } from '@/models/UserTypes';
import { logger } from '@/utils/logger';

export const createUserInFirestore = async (
  uid: string,
  email: string,
  consent?: {
    marketingEmailConsent: boolean;
    transactionalEmailConsent?: boolean;
    termsAcceptedAt: string;
  },
  providerData?: {
    name?: string;
    photoURL?: string;
    provider?: string;
  }
): Promise<void> => {
  const userRef = doc(db, 'users', uid);
  const initialProfile: UserProfile = {
      name: providerData?.name || '',
      examDate: '',
      studyGoal: '',
      // Name from Google/Apple is NOT onboarding. True only after the 6Q +
      // profile write (createTrialUser / updateUserProfile diagnostic persist).
      onboardingCompleted: false,
      lastUpdated: ''
  };
  
  try {
    await setDoc(userRef, {
      email: email,
      profile: initialProfile,
      createdAt: new Date().toISOString(),
      subscriptionStatus: 'inactive',
      testResults: {},
      wrongAnswers: [],
      // Initialize dashboard metrics to prevent showing zeros
      totalQuestionsAttempted: 0,
      correctAnswers: 0,
      averageScore: 0,
      masteryLevel: 0,
      studyStreak: 0,
      totalStudyHours: 0,
      completedTests: 0,
      categoryStats: {},
      metricsInitialized: true,
      metricsVersion: '5.0',
      // Store provider information for OAuth users
      ...(providerData && {
        providerData: {
          photoURL: providerData.photoURL || '',
          provider: providerData.provider || 'email',
          lastUpdated: new Date().toISOString()
        }
      }),
      // Store consent information
      consent: consent ? {
        marketingEmailConsent: consent.marketingEmailConsent,
        transactionalEmailConsent: consent.transactionalEmailConsent ?? true,
        termsAcceptedAt: consent.termsAcceptedAt,
        consentIpAddress: '', // You might want to capture IP in production
        lastUpdated: new Date().toISOString()
      } : {
        marketingEmailConsent: false,
        transactionalEmailConsent: true,
        termsAcceptedAt: new Date().toISOString(),
        lastUpdated: new Date().toISOString()
      }
    });
  } catch (error) {
    logger.error('FirebaseUser', 'Error creating user in Firestore', { error });
    throw new Error('Failed to create user profile');
  }
};

export const updateUserProfile = async (
  userId: string,
  profile: Partial<UserProfile>
): Promise<void> => {
  if (!userId) throw new Error('No user ID provided');

  const userDocRef = doc(db, 'users', userId);
  try {
    // Get current profile first
    const userDoc = await getDoc(userDocRef);
    if (!userDoc.exists()) {
      throw new Error('User document not found');
    }

    const currentProfile = userDoc.data().profile || {};
    
    // Merge current profile with updates
    await updateDoc(userDocRef, {
      profile: {
        ...currentProfile,
        ...profile,
        lastUpdated: new Date().toISOString()
      }
    });
  } catch (error) {
    logger.error('FirebaseUser', 'Error updating user profile', { error });
    throw new Error('Failed to update user profile');
  }
};

// NOTE: raw-doc access helpers (`checkSubscriptionStatus`,
// `setupUserStatusListener`) were REMOVED. They passed a raw Firestore doc to
// `hasFullAccess`, which — since a raw doc never carries the server-computed
// `hasFullAccess` flag — always returned false and would have locked out paying
// users. They had no callers. Read the computed `hasFullAccess` from
// getUserProfile (or the AuthContext `userData`) instead.

// Helper function to get current user ID safely
export const getCurrentUserId = (): string => {
  const user = auth.currentUser;
  if (!user) throw new Error('No authenticated user');
  return user.uid;
}; 